import { CardContent } from "@/components/CardContent";
import { HeaderPage } from "@/components/HeaderPage";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Download,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle2,
  AlertTriangle,
  BarChart3,
  Calendar,
  FileText,
  RefreshCw,
  Search,
  Users,
  UserPlus,
  UserCheck,
  Activity,
  MapPin as MapPinIcon,
} from "lucide-react";
import { LuClock10 } from "react-icons/lu";
import { IoWarningOutline } from "react-icons/io5";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart as RechartsPie,
  Pie,
  Cell,
} from "recharts";
import { motion } from "framer-motion";
import { useState, useMemo } from "react";
import { HeatmapLeaflet } from "@/components/HeatmapLeaflet";
import { Loading } from "@/components/Loading";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import LogoMunicipal from "@/assets/logo_municipal.svg";
import {
  useGetReportsKPISData,
  useGetReportDemandEvolution,
  useGetReportSectorDistribution,
  useGetReportCategoryRanking,
  useGetReportSectorPerformance,
  useGetReportUserMetrics,
} from "@/hooks/useReports";
import { useGetOccurrencesHeatmapData } from "@/hooks/useDashboard";
import { useGetSectors } from "@/hooks/useSectors";
import { useGetCategories } from "@/hooks/useCategories";

const COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
];

// Funções utilitárias para datas
const getFirstDayOfWeek = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Domingo = 0, Segunda = 1
  const firstDay = new Date(now);
  firstDay.setDate(now.getDate() + diff);
  return firstDay.toISOString().split("T")[0];
};

const getLastDayOfWeek = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const diff = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
  const lastDay = new Date(now);
  lastDay.setDate(now.getDate() + diff);
  return lastDay.toISOString().split("T")[0];
};

export const Relatorios = () => {
  const [showPercentage, setShowPercentage] = useState(false);
  const [startDate, setStartDate] = useState(getFirstDayOfWeek());
  const [endDate, setEndDate] = useState(getLastDayOfWeek());
  const [isExporting, setIsExporting] = useState(false);
  const [sectorId, setSectorId] = useState<string>("all");
  const [categoryId, setCategoryId] = useState<string>("all");

  const { data: sectorsData } = useGetSectors({ limit: 100 });
  const { data: categoriesData } = useGetCategories();

  const handleRefresh = () => {
    refetchKPIs();
    refetchEvolution();
    refetchSectorDistribution();
    refetchCategoryRanking();
    refetchSectorPerformance();
    refetchUserMetrics();
    refetchHeatmap();
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const doc = new jsPDF();
      let yPos = 20;

      // Adicionar logo
      const img = new Image();
      img.src = LogoMunicipal;
      await new Promise((resolve) => {
        img.onload = resolve;
      });

      doc.addImage(img, "PNG", 85, yPos, 40, 40);
      yPos += 45;

      // Cabeçalho
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.text("Vida+Simples", 105, yPos, { align: "center" });
      yPos += 8;

      doc.setFontSize(16);
      doc.setFont("helvetica", "normal");
      doc.text("Relatório de Ocorrências", 105, yPos, { align: "center" });
      yPos += 7;

      doc.setFontSize(10);
      doc.setTextColor(100);

      // Formatar datas corretamente sem problema de timezone
      const formatDate = (dateStr: string) => {
        const [year, month, day] = dateStr.split("-");
        return `${day}/${month}/${year}`;
      };

      doc.text(
        `Período: ${formatDate(startDate)} a ${formatDate(endDate)}`,
        105,
        yPos,
        { align: "center" },
      );
      yPos += 5;
      doc.text(
        `Gerado em: ${new Date().toLocaleDateString("pt-BR")} às ${new Date().toLocaleTimeString("pt-BR")}`,
        105,
        yPos,
        { align: "center" },
      );
      yPos += 10;

      // Linha divisória
      doc.setDrawColor(200);
      doc.line(20, yPos, 190, yPos);
      yPos += 10;

      // Resumo Executivo
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0);
      doc.text("Resumo Executivo", 20, yPos);
      yPos += 8;

      const kpiData = [
        ["Abertas Hoje", kpisData?.occurrencesOpenedToday || 0],
        ["Concluídas Hoje", kpisData?.completedToday || 0],
        ["Em Andamento", kpisData?.inProgress || 0],
        ["Em Atraso (SLA)", kpisData?.delayed || 0],
        [
          "Tempo Médio de Atendimento",
          kpisData?.averageTimeHours
            ? `${(kpisData.averageTimeHours / 24).toFixed(1)} dias`
            : "0 dias",
        ],
        [
          "Taxa de Reabertura",
          kpisData?.reopeningRate
            ? `${kpisData.reopeningRate.toFixed(1)}%`
            : "0%",
        ],
        ["Total de Ocorrências", kpisData?.totalOccurrences || 0],
        ["Ocorrências Resolvidas", kpisData?.resolvedOccurrences || 0],
        ["Ocorrências Pendentes", kpisData?.pendingOccurrences || 0],
        [
          "Tempo Médio de Resolução",
          `${kpisData?.averageResolutionTime || 0}h`,
        ],
      ];

      autoTable(doc, {
        startY: yPos,
        head: [["Métrica", "Valor"]],
        body: kpiData,
        theme: "grid",
        headStyles: {
          fillColor: [59, 130, 246],
          fontSize: 10,
          fontStyle: "bold",
        },
        styles: { fontSize: 9 },
        margin: { left: 20, right: 20 },
      });

      yPos = (doc as any).lastAutoTable.finalY + 10;

      // Distribuição por Setor
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Distribuição por Setor", 20, yPos);
      yPos += 8;

      const sectorData = sectorChartData.map((item: any) => {
        const total = sectorChartData.reduce(
          (sum: number, d: any) => sum + d.value,
          0,
        );
        const percentage = ((item.value / total) * 100).toFixed(1);
        return [item.name, item.value, `${percentage}%`];
      });

      autoTable(doc, {
        startY: yPos,
        head: [["Setor", "Quantidade", "Percentual"]],
        body: sectorData,
        theme: "striped",
        headStyles: {
          fillColor: [59, 130, 246],
          fontSize: 10,
          fontStyle: "bold",
        },
        styles: { fontSize: 9 },
        margin: { left: 20, right: 20 },
      });

      yPos = (doc as any).lastAutoTable.finalY + 10;

      // Top Categorias
      // Verificar se há espaço suficiente (precisa de pelo menos 120mm para a tabela completa)
      if (yPos > 170) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Top 10 Categorias de Problemas", 20, yPos);
      yPos += 8;

      const categoryData = (categoryRankingData || [])
        .slice(0, 10)
        .map((item: any, index: number) => [
          `#${index + 1}`,
          item.name,
          item.value,
        ]);

      autoTable(doc, {
        startY: yPos,
        head: [["Pos.", "Categoria", "Quantidade"]],
        body: categoryData,
        theme: "striped",
        headStyles: {
          fillColor: [59, 130, 246],
          fontSize: 10,
          fontStyle: "bold",
        },
        styles: { fontSize: 9 },
        margin: { left: 20, right: 20 },
      });

      yPos = (doc as any).lastAutoTable.finalY + 10;

      // Desempenho por Setor
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Desempenho por Setor", 20, yPos);
      yPos += 8;

      const performanceData = (sectorPerformanceData || []).map((item: any) => [
        item.sectorName || "-",
        item.open || 0,
        item.inProgress || 0,
        item.delayed || 0,
        `${item.slaCompliancePercentage || 0}%`,
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [["Setor", "Abertas", "Em Andamento", "Atrasadas", "SLA"]],
        body: performanceData,
        theme: "grid",
        headStyles: {
          fillColor: [59, 130, 246],
          fontSize: 9,
          fontStyle: "bold",
        },
        styles: { fontSize: 8 },
        margin: { left: 20, right: 20 },
      });

      yPos = (doc as any).lastAutoTable.finalY + 10;

      // Métricas de Usuários
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Métricas de Usuários do Sistema", 20, yPos);
      yPos += 8;

      const userMetrics = [
        ["Total de Usuários", userMetricsData?.totalUsers || 0],
        ["Novos Usuários (Período)", userMetricsData?.newUsers || 0],
        ["Usuários Ativos", userMetricsData?.activeUsers || 0],
        [
          "Média de Ocorrências por Usuário",
          userMetricsData?.averageOccurrencesPerUser?.toFixed(1) || "0.0",
        ],
      ];

      autoTable(doc, {
        startY: yPos,
        head: [["Métrica", "Valor"]],
        body: userMetrics,
        theme: "grid",
        headStyles: {
          fillColor: [59, 130, 246],
          fontSize: 10,
          fontStyle: "bold",
        },
        styles: { fontSize: 9 },
        margin: { left: 20, right: 20 },
      });

      // Rodapé em todas as páginas
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
          "Este relatório foi gerado automaticamente pelo sistema de gerenciamento de ocorrências.",
          105,
          285,
          { align: "center" },
        );
        doc.text(`Página ${i} de ${pageCount}`, 105, 290, { align: "center" });
      }

      // Salvar PDF
      doc.save(
        `relatorio-frecheirinha-${new Date().toISOString().split("T")[0]}.pdf`,
      );
    } catch (error) {
      console.error("Erro ao exportar PDF:", error);
      alert("Erro ao exportar relatório para PDF");
    } finally {
      setIsExporting(false);
    }
  };

  const {
    data: kpisData,
    isLoading: loadingKPIs,
    refetch: refetchKPIs,
  } = useGetReportsKPISData({
    startDate,
    endDate,
    sectorId: sectorId !== "all" ? sectorId : undefined,
    categoryId: categoryId !== "all" ? categoryId : undefined,
  });

  const {
    data: demandEvolutionData,
    isLoading: loadingEvolution,
    refetch: refetchEvolution,
  } = useGetReportDemandEvolution({
    startDate,
    endDate,
    sectorId: sectorId !== "all" ? sectorId : undefined,
    categoryId: categoryId !== "all" ? categoryId : undefined,
  });

  const {
    data: sectorDistributionData,
    isLoading: loadingSectorDistribution,
    refetch: refetchSectorDistribution,
  } = useGetReportSectorDistribution({
    startDate,
    endDate,
  });

  const {
    data: categoryRankingData,
    isLoading: loadingCategoryRanking,
    refetch: refetchCategoryRanking,
  } = useGetReportCategoryRanking({
    startDate,
    endDate,
    valueType: showPercentage ? "percentage" : "quantity",
    sectorId: sectorId !== "all" ? sectorId : undefined,
  });

  const {
    data: sectorPerformanceData,
    isLoading: loadingSectorPerformance,
    refetch: refetchSectorPerformance,
  } = useGetReportSectorPerformance({
    startDate,
    endDate,
  });

  const {
    data: userMetricsData,
    isLoading: loadingUserMetrics,
    refetch: refetchUserMetrics,
  } = useGetReportUserMetrics({
    startDate,
    endDate,
  });

  const {
    data: heatmapData,
    isLoading: loadingHeatmap,
    refetch: refetchHeatmap,
  } = useGetOccurrencesHeatmapData({
    startDate,
    endDate,
    sectorId: sectorId !== "all" ? sectorId : undefined,
    categoryId: categoryId !== "all" ? categoryId : undefined,
  });

  // Transformar dados da API para o formato do gráfico
  const evolutionChartData = useMemo(() => {
    if (!demandEvolutionData?.data) return [];
    return demandEvolutionData.data.map((item: any) => {
      // Formatar data diretamente da string para evitar problema de timezone
      const [year, month, day] = item.date.split("-");
      return {
        periodo: `${day}/${month}`,
        abertas: item.opened,
        concluidas: item.completed,
        emAtraso: item.delayed,
      };
    });
  }, [demandEvolutionData]);

  // Transformar dados da distribuição por setor
  const sectorChartData = useMemo(() => {
    if (!sectorDistributionData) return [];
    // Se os dados vêm direto como array, usa direto; se vêm em .data, usa .data
    return Array.isArray(sectorDistributionData)
      ? sectorDistributionData
      : sectorDistributionData.data || [];
  }, [sectorDistributionData]);

  // Transformar dados do ranking de categorias
  const categoryChartData = useMemo(() => {
    if (!categoryRankingData) return [];
    return Array.isArray(categoryRankingData)
      ? categoryRankingData
      : categoryRankingData.data || [];
  }, [categoryRankingData]);

  // Transformar dados de desempenho por setor
  const sectorPerformanceTableData = useMemo(() => {
    if (!sectorPerformanceData) return [];
    const data = Array.isArray(sectorPerformanceData)
      ? sectorPerformanceData
      : sectorPerformanceData.data || [];

    return data.map((sector: any) => ({
      name: sector.sectorName,
      abertas: sector.open,
      andamento: sector.inProgress,
      atraso: sector.delayed,
      sla: sector.slaCompliancePercentage,
    }));
  }, [sectorPerformanceData]);

  return (
    <>
      <HeaderPage
        title="Relatórios"
        description="Acompanhe métricas e indicadores de desempenho"
      />

      {/* Filters */}
      <motion.div
        className="flex flex-col sm:flex-row gap-3 mb-6 mt-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex gap-3 flex-1">
          <div className="flex-1">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-9"
            />
          </div>
          <div className="flex-1">
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="h-9"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <Select value={sectorId} onValueChange={setSectorId}>
            <SelectTrigger className="w-[180px] h-9">
              <SelectValue placeholder="Todos os setores" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os setores</SelectItem>
              {sectorsData?.data?.map((sector: any) => (
                <SelectItem key={sector.id} value={sector.id}>
                  {sector.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={categoryId} onValueChange={setCategoryId}>
            <SelectTrigger className="w-[180px] h-9">
              <SelectValue placeholder="Todas as categorias" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as categorias</SelectItem>
              {categoriesData?.data?.map((category: any) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            className="h-9 cursor-pointer"
            onClick={handleRefresh}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
          <Button
            size="sm"
            className="h-9 text-white cursor-pointer"
            onClick={handleExportPDF}
            disabled={isExporting}
          >
            <Download className="h-4 w-4 mr-2" />
            {isExporting ? "Exportando..." : "Exportar PDF"}
          </Button>
        </div>
      </motion.div>

      {/* KPI Cards */}
      <motion.div
        className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        {/* Ocorrências abertas hoje */}
        <div className="bg-white w-full h-[120px] px-5 py-4 rounded-xl shadow-sm flex justify-between gap-2 border border-gray-300">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-normal text-gray-600">
              Abertas Hoje
            </span>
            <span className="text-3xl font-bold">
              {loadingKPIs ? "..." : kpisData?.occurrencesOpenedToday || 0}
            </span>
            <div className="flex items-center gap-1 text-xs text-blue-600">
              <Calendar className="h-3 w-3" />
              <span>Últimas 24h</span>
            </div>
          </div>
          <div className="bg-blue-600/10 px-2.5 py-2.5 rounded-xl self-start">
            <FileText size={24} className="text-blue-600" />
          </div>
        </div>

        {/* Concluídas hoje */}
        <div className="bg-white w-full h-[120px] px-5 py-4 rounded-xl shadow-sm flex justify-between gap-2 border border-gray-300">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-normal text-gray-600">
              Concluídas Hoje
            </span>
            <span className="text-3xl font-bold">
              {loadingKPIs ? "..." : kpisData?.completedToday || 0}
            </span>
            <div className="flex items-center gap-1 text-xs text-green-600">
              {loadingKPIs ? (
                <span>Carregando...</span>
              ) : kpisData?.completedTodayDiff ? (
                <>
                  {kpisData.completedTodayDiff > 0 ? (
                    <TrendingUp className="h-3 w-3" />
                  ) : kpisData.completedTodayDiff < 0 ? (
                    <TrendingDown className="h-3 w-3" />
                  ) : null}
                  <span>
                    {kpisData.completedTodayDiff > 0 ? "+" : ""}
                    {kpisData.completedTodayDiff} vs ontem
                  </span>
                </>
              ) : (
                <span>Sem dados anteriores</span>
              )}
            </div>
          </div>
          <div className="bg-green-600/10 px-2.5 py-2.5 rounded-xl self-start">
            <CheckCircle2 size={24} className="text-green-600" />
          </div>
        </div>

        {/* Em Andamento (total) */}
        <div className="bg-white w-full h-[120px] px-5 py-4 rounded-xl shadow-sm flex justify-between gap-2 border border-gray-300">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-normal text-gray-600">
              Em Andamento
            </span>
            <span className="text-3xl font-bold">
              {loadingKPIs ? "..." : kpisData?.inProgress || 0}
            </span>
            <div className="flex items-center gap-1 text-xs text-blue-600">
              <Clock className="h-3 w-3" />
              <span>Total ativo</span>
            </div>
          </div>
          <div className="bg-blue-500/10 px-2.5 py-2.5 rounded-xl self-start">
            <LuClock10 size={24} className="text-blue-500" />
          </div>
        </div>

        {/* Em Atraso (SLA vencido) */}
        <div className="bg-white w-full h-[120px] px-5 py-4 rounded-xl shadow-sm flex justify-between gap-2 border border-gray-300">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-normal text-gray-600">
              Em Atraso (SLA)
            </span>
            <span className="text-3xl font-bold">
              {loadingKPIs ? "..." : kpisData?.delayed || 0}
            </span>
            <div className="flex items-center gap-1 text-xs text-red-600">
              <AlertTriangle className="h-3 w-3" />
              <span>SLA vencido</span>
            </div>
          </div>
          <div className="bg-red-500/10 px-2.5 py-2.5 rounded-xl self-start">
            <AlertTriangle size={24} className="text-red-500" />
          </div>
        </div>

        {/* Tempo médio de atendimento */}
        <div className="bg-white w-full h-[120px] px-5 py-4 rounded-xl shadow-sm flex justify-between gap-2 border border-gray-300">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-normal text-gray-600">
              Tempo Médio
            </span>
            <span className="text-3xl font-bold">
              {loadingKPIs
                ? "..."
                : kpisData?.averageTimeHours
                  ? `${(kpisData.averageTimeHours / 24).toFixed(1)} dias`
                  : "0 dias"}
            </span>
            <div
              className={`flex items-center gap-1 text-xs ${
                loadingKPIs
                  ? "text-gray-500"
                  : kpisData?.averageTimeDiffDays &&
                      kpisData.averageTimeDiffDays < 0
                    ? "text-green-600"
                    : "text-red-600"
              }`}
            >
              {loadingKPIs ? (
                <span>Carregando...</span>
              ) : kpisData?.averageTimeDiffDays !== undefined &&
                kpisData?.averageTimeDiffDays !== null ? (
                <>
                  {kpisData.averageTimeDiffDays < 0 ? (
                    <TrendingDown className="h-3 w-3" />
                  ) : kpisData.averageTimeDiffDays > 0 ? (
                    <TrendingUp className="h-3 w-3" />
                  ) : null}
                  <span>
                    {kpisData.averageTimeDiffDays > 0 ? "+" : ""}
                    {kpisData.averageTimeDiffDays.toFixed(1)} dias
                  </span>
                </>
              ) : (
                <span>Sem dados anteriores</span>
              )}
            </div>
          </div>
          <div className="bg-purple-500/10 px-2.5 py-2.5 rounded-xl self-start">
            <Clock size={24} className="text-purple-500" />
          </div>
        </div>

        {/* Taxa de reabertura */}
        <div className="bg-white w-full h-[120px] px-5 py-4 rounded-xl shadow-sm flex justify-between gap-2 border border-gray-300">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-normal text-gray-600">
              Taxa de Reabertura
            </span>
            <span className="text-3xl font-bold">
              {loadingKPIs
                ? "..."
                : kpisData?.reopeningRate
                  ? `${kpisData.reopeningRate.toFixed(1)}%`
                  : "0%"}
            </span>
            <div className="flex items-center gap-1 text-xs text-orange-600">
              <RefreshCw className="h-3 w-3" />
              <span>
                {loadingKPIs
                  ? "Carregando..."
                  : `${kpisData?.totalReopenings || 0} reaberturas`}
              </span>
            </div>
          </div>
          <div className="bg-orange-500/10 px-2.5 py-2.5 rounded-xl self-start">
            <IoWarningOutline size={24} className="text-orange-500" />
          </div>
        </div>
      </motion.div>

      {/* Charts Row 1 */}
      <motion.div
        className="flex w-full gap-4 mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
      >
        <CardContent
          title="Evolução de Demandas"
          content={
            <div>
              <div className="h-[250px]">
                {loadingEvolution ? (
                  <div className="flex items-center justify-center h-full">
                    <span className="text-muted-foreground">Carregando...</span>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={evolutionChartData}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="hsl(var(--border))"
                      />
                      <XAxis
                        dataKey="periodo"
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                      />
                      <YAxis
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                        }}
                        formatter={(value: any, name?: string) => {
                          const labels: Record<string, string> = {
                            abertas: "Demandas abertas",
                            concluidas: "Demandas concluídas",
                            emAtraso: "Demandas em atraso",
                          };
                          return [value, labels[name || ""] || name];
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="abertas"
                        stroke="#3b82f6"
                        strokeWidth={2.5}
                        dot={{ fill: "#3b82f6", strokeWidth: 2, r: 4 }}
                        name="abertas"
                        activeDot={{ r: 6 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="concluidas"
                        stroke="#10b981"
                        strokeWidth={2.5}
                        dot={{ fill: "#10b981", strokeWidth: 2, r: 4 }}
                        name="concluidas"
                        activeDot={{ r: 6 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="emAtraso"
                        stroke="#ef4444"
                        strokeWidth={2.5}
                        dot={{ fill: "#ef4444", strokeWidth: 2, r: 4 }}
                        name="emAtraso"
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
              <div className="flex justify-center gap-6 mt-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="text-xs text-gray-600">
                    Demandas abertas
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-xs text-gray-600">
                    Demandas concluídas
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="text-xs text-gray-600">
                    Demandas em atraso
                  </span>
                </div>
              </div>
            </div>
          }
        />

        <CardContent
          title="Distribuição por Setor"
          content={
            <div className="h-[300px]">
              {loadingSectorDistribution ? (
                <div className="flex items-center justify-center h-full">
                  <span className="text-muted-foreground">Carregando...</span>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={sectorChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={(entry: any) =>
                        `${entry.name} ${(entry.percentage ?? 0).toFixed(1)}%`
                      }
                      labelLine={false}
                    >
                      {sectorChartData.map((_: any, index: number) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                      formatter={(value: any, name?: string) => {
                        if (name === "value") return [value, "Ocorrências"];
                        if (name === "percentage")
                          return [`${value}%`, "Percentual"];
                        return [value, name];
                      }}
                    />
                  </RechartsPie>
                </ResponsiveContainer>
              )}
            </div>
          }
        />
      </motion.div>

      {/* Charts Row 2 */}
      <motion.div
        className="flex w-full gap-4 mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.3 }}
      >
        <CardContent
          title="Top Categorias de Problemas"
          content={
            <div>
              <div className="flex justify-end mb-3">
                <div className="flex gap-2">
                  <Button
                    variant={!showPercentage ? "default" : "outline"}
                    size="sm"
                    onClick={() => setShowPercentage(false)}
                    className={
                      !showPercentage
                        ? "text-white cursor-pointer"
                        : "cursor-pointer"
                    }
                  >
                    Quantidade
                  </Button>
                  <Button
                    variant={showPercentage ? "default" : "outline"}
                    size="sm"
                    onClick={() => setShowPercentage(true)}
                    className={
                      showPercentage
                        ? "text-white cursor-pointer"
                        : "cursor-pointer"
                    }
                  >
                    Percentual
                  </Button>
                </div>
              </div>
              <div className="h-[250px]">
                {loadingCategoryRanking ? (
                  <div className="flex items-center justify-center h-full">
                    <span className="text-muted-foreground">Carregando...</span>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={categoryChartData} layout="vertical">
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="hsl(var(--border))"
                      />
                      <XAxis
                        type="number"
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                        tickFormatter={(value) =>
                          showPercentage ? `${value}%` : value.toString()
                        }
                      />
                      <YAxis
                        dataKey="name"
                        type="category"
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={11}
                        width={130}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                        }}
                        formatter={(value: any) => [
                          showPercentage ? `${value}%` : value,
                          showPercentage ? "Percentual" : "Quantidade",
                        ]}
                      />
                      <Bar
                        dataKey="value"
                        fill="hsl(var(--primary))"
                        radius={[0, 4, 4, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          }
        />

        <CardContent
          title="Desempenho por Setor"
          content={
            <>
              <div className="flex justify-end mb-4">
                <Button variant="outline" size="sm">
                  <FileText className="h-4 w-4 mr-2" />
                  Exportar CSV
                </Button>
              </div>
              {loadingSectorPerformance ? (
                <div className="flex items-center justify-center h-[200px]">
                  <span className="text-muted-foreground">Carregando...</span>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                          Setor
                        </th>
                        <th className="text-center py-3 px-4 font-medium text-muted-foreground">
                          Abertas
                        </th>
                        <th className="text-center py-3 px-4 font-medium text-muted-foreground">
                          Andamento
                        </th>
                        <th className="text-center py-3 px-4 font-medium text-muted-foreground">
                          Atraso
                        </th>
                        <th className="text-center py-3 px-4 font-medium text-muted-foreground">
                          SLA %
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sectorPerformanceTableData.map((sector) => {
                        const slaBelowTarget = sector.sla < 80;
                        return (
                          <tr
                            key={sector.name}
                            className="border-b last:border-0 hover:bg-muted/50 transition-colors cursor-pointer"
                            onClick={() => {
                              console.log("Detalhes do setor:", sector.name);
                              // Aqui você pode adicionar navegação ou abrir modal com detalhes
                            }}
                          >
                            <td className="py-3 px-4 font-medium">
                              {sector.name}
                            </td>
                            <td className="text-center py-3 px-4">
                              <Badge
                                variant="outline"
                                className="bg-blue-50 text-blue-700 border-blue-200"
                              >
                                {sector.abertas}
                              </Badge>
                            </td>
                            <td className="text-center py-3 px-4">
                              <Badge
                                variant="outline"
                                className="bg-purple-50 text-purple-700 border-purple-200"
                              >
                                {sector.andamento}
                              </Badge>
                            </td>
                            <td className="text-center py-3 px-4">
                              <Badge
                                variant="outline"
                                className="bg-orange-50 text-orange-700 border-orange-200"
                              >
                                {sector.atraso}
                              </Badge>
                            </td>
                            <td className="text-center py-3 px-4">
                              <Badge
                                variant="outline"
                                className={
                                  slaBelowTarget
                                    ? "bg-red-100 text-red-700 border-red-300 font-semibold"
                                    : "bg-green-100 text-green-700 border-green-300"
                                }
                              >
                                {sector.sla.toFixed(2)}%
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          }
        />
      </motion.div>

      {/* Mapa de Calor */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.5 }}
        className="mb-6 mt-5"
      >
        <CardContent
          title="Mapa de Calor - Regiões com Maior Incidência"
          content={
            loadingHeatmap ? (
              <div className="flex items-center justify-center h-[400px]">
                <Loading label="Carregando mapa de calor..." />
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPinIcon className="h-4 w-4" />
                  <span>
                    Visualização geográfica de {heatmapData?.length || 0}{" "}
                    ocorrências
                  </span>
                </div>
                <HeatmapLeaflet
                  points={
                    heatmapData?.map((item: any) => [
                      item.latitude,
                      item.longitude,
                      item.intensity || 1,
                    ]) || []
                  }
                  height="400px"
                />
                <div className="flex items-center justify-center gap-6 text-xs text-muted-foreground pt-2">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-blue-500" />
                    <span>Baixa Incidência</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-yellow-500" />
                    <span>Média Incidência</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-red-500" />
                    <span>Alta Incidência</span>
                  </div>
                </div>
              </div>
            )
          }
        />
      </motion.div>

      {/* User Metrics Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.6 }}
        className="mb-6 mt-5"
      >
        <h3 className="text-lg font-semibold mb-4">Métricas de Usuários</h3>
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          {/* Usuários cadastrados (total) */}
          <div className="bg-white w-full h-[120px] px-5 py-4 rounded-xl shadow-sm flex justify-between gap-2 border border-gray-300">
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-normal text-gray-600">
                Usuários Cadastrados
              </span>
              <span className="text-2xl font-bold">
                {loadingUserMetrics
                  ? "..."
                  : userMetricsData?.totalUsers?.toLocaleString("pt-BR") || "0"}
              </span>
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <span>Total acumulado</span>
              </div>
            </div>
            <div className="bg-indigo-600/10 px-2.5 py-2.5 rounded-xl self-start">
              <Users size={24} className="text-indigo-600" />
            </div>
          </div>

          {/* Novos usuários (período) */}
          <div className="bg-white w-full h-[120px] px-5 py-4 rounded-xl shadow-sm flex justify-between gap-2 border border-gray-300">
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-normal text-gray-600">
                Novos Usuários
              </span>
              <span className="text-2xl font-bold">
                {loadingUserMetrics
                  ? "..."
                  : userMetricsData?.newUsers?.toLocaleString("pt-BR") || "0"}
              </span>
              <div className="flex items-center gap-1 text-xs text-blue-600">
                <TrendingUp className="h-3 w-3" />
                <span>No período</span>
              </div>
            </div>
            <div className="bg-blue-600/10 px-2.5 py-2.5 rounded-xl self-start">
              <UserPlus size={24} className="text-blue-600" />
            </div>
          </div>

          {/* Usuários ativos (período) */}
          <div className="bg-white w-full h-[120px] px-5 py-4 rounded-xl shadow-sm flex justify-between gap-2 border border-gray-300">
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-normal text-gray-600">
                Usuários Ativos
              </span>
              <span className="text-2xl font-bold">
                {loadingUserMetrics
                  ? "..."
                  : userMetricsData?.activeUsers?.toLocaleString("pt-BR") ||
                    "0"}
              </span>
              <div className="flex items-center gap-1 text-xs text-green-600">
                <Activity className="h-3 w-3" />
                <span>No período</span>
              </div>
            </div>
            <div className="bg-green-600/10 px-2.5 py-2.5 rounded-xl self-start">
              <UserCheck size={24} className="text-green-600" />
            </div>
          </div>

          {/* Ocorrências por usuário (média) */}
          <div className="bg-white w-full h-[120px] px-5 py-4 rounded-xl shadow-sm flex justify-between gap-2 border border-gray-300">
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-normal text-gray-600">
                Média por Usuário
              </span>
              <span className="text-2xl font-bold">
                {loadingUserMetrics
                  ? "..."
                  : userMetricsData?.averageOccurrencesPerUser?.toFixed(1) ||
                    "0.0"}
              </span>
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <span>ocorrências</span>
              </div>
            </div>
            <div className="bg-purple-600/10 px-2.5 py-2.5 rounded-xl self-start">
              <BarChart3 size={24} className="text-purple-600" />
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
};
